import {AuthStorageWrapper} from "./AuthStorageWrapper";
import Utils from "../utils/Utils";
import {storageConstants} from "./AuthConstants";
import { logConstants } from "../utils/Constants";
import {DomainCookieTableEntry, HeadersJson, Cookie} from "./dsAuth/data/AuthTypes";
import {CookieLookUpParams} from "./dsAuth/data/AuthTypes";
import {HeaderParser} from "./dsAuth/parser/HeaderParser";
import {Logger} from "./Logger";
import { environment } from "./environment";

const utils = Utils.getInstance();

export class CookieManager {
         private static instance: CookieManager;

         /// <summary>
         /// Create a new instance of CookieManager if not already present.
         /// </summary>
         static getInstance(): CookieManager {
           if (!CookieManager.instance) {
             CookieManager.instance = new CookieManager();
           }
           return CookieManager.instance;
         }

         /// <summary>
         /// parses set cookie header and stores the cookies in db
         /// </summary>
         async storeCookiesFromHeader(
           dsAuthChallengeHeaders: HeadersJson,
           secondaryUrl: string,
           referenceUri: string
         ): Promise<void> {
           const cookies: Cookie[] = HeaderParser.getInstance().parseCookieHeader(
             dsAuthChallengeHeaders
           );
           if (cookies) {
             await this.storeCookies(
               cookies,
               secondaryUrl,
               referenceUri
             );
           }
         }

         /// <summary>
         /// check the domain of cookies to store, check if db contains any cookies for this domain
         /// if yes, append new cookies to same db entry else create new entry
         /// </summary>
         async storeCookies(
           cookies: Cookie[],
           url: string,
           referenceUri: string
         ): Promise<void> {
           for (const cookieEntry of cookies) {
             let domain: string = null;
             if (cookieEntry.domain) {
               domain = cookieEntry.domain;
             }

             if (!domain) {
               domain = utils.getHost(url);
             }

             const cookiesList = await this.getAllCookiesForURL(
               url,
               referenceUri
             );
             
             await this.appendCookie(
              cookiesList,
              cookieEntry,
              domain,
              referenceUri
             );
           }
         }

         //checking cookie expiry inside appendCookie so that we clear previously stored cookie upon receiving expired cookie with same cookie name
         async appendCookie(
           cookiesFromTable: Cookie[],
           cookieToBeAppended: Cookie,
           domain: string,
           referenceUri: string
         ): Promise<void> {
           if (Array.isArray(cookiesFromTable)) {
             let cookieWithSameNameFoundInTable = false;
             const isCookieToBeAppendedExpired = this.isCookieExpired(cookieToBeAppended);
             for (const index in cookiesFromTable) {
               let domain1; 
               if (
                 cookieToBeAppended.name == cookiesFromTable[index].name
               ) {
                 cookieWithSameNameFoundInTable = true;
                 if (!isCookieToBeAppendedExpired) {
                   cookiesFromTable[index] = cookieToBeAppended;
                 } else {
                   //cookie expired
                    if(
                      cookiesFromTable[index].domain
                    ) {
                        domain1 = cookiesFromTable[index].domain;
                    }
                   cookiesFromTable.splice(Number(index), 1);
                 }
                 const finalDomain = domain1 ? domain1 : domain;
                 if (cookiesFromTable.length > 0) {
                   await this.storeCookiesForDomain(
                    finalDomain,
                     cookiesFromTable,
                     referenceUri
                   );
                 } else {
                   const authStorage = (await AuthStorageWrapper.getInstance()).getInMemoryStorage();
                   await authStorage.deleteEntry(
                     storageConstants.DOMAIN_COOKIE_TABLE,
                     [finalDomain, referenceUri]
                   );
                 }
                 break;
               }
             }
             if (!cookieWithSameNameFoundInTable && !isCookieToBeAppendedExpired) {
               cookiesFromTable.push(cookieToBeAppended);
               await this.storeCookiesForDomain(
                 domain,
                 cookiesFromTable,
                 referenceUri
               );
             }
           }
         }

         async encryptCookies(cookieArray: Cookie[], referenceUri: string): Promise<Cookie[]> {
          return Promise.all(cookieArray.map(async (cookie) => {
            cookie.value = await environment.encrypt(referenceUri, cookie.value);
            return cookie;
          }));
        }

        async decryptCookies(cookieArray: Cookie[], referenceUri: string): Promise<Cookie[]> {
          return Promise.all(cookieArray.map(async (cookie) => {
            cookie.value = await environment.decrypt(referenceUri, cookie.value);
            return cookie;
          }));
        }

         /// <summary>
         /// method which updates the DomainCookieTable
         /// </summary>
         async storeCookiesForDomain(
           domain: string,
           cookieArray: Cookie[],
           referenceUri: string
         ): Promise<void> {
           //ignoring leading dots in domain name as per set-cookie rules
           if (domain[0] === ".") {
             domain = domain.substring(1);
           }
           const encryptedCookiees = await this.encryptCookies(cookieArray, referenceUri);
           const domainTableEntry: DomainCookieTableEntry = {
             domain: domain,
             referenceUri: referenceUri,
             cookie: encryptedCookiees,
             };
           const authStorage = (await AuthStorageWrapper.getInstance()).getInMemoryStorage();
           await authStorage.updateEntry(
             storageConstants.DOMAIN_COOKIE_TABLE,
             domainTableEntry
           );
         }

         /// <summary>
         /// get all cookies for given url
         /// Returns an array of cookies
         /// </summary>
         async getAllCookiesForURL(
           url: string,
           referenceUri: string
         ): Promise<Cookie[]> {
           const host = utils.getHost(url);
           const hostComponents = host.split(".");
           let allCookies: Cookie[] = [];
           let domain = "";
           const isSecureURL = utils.isHTTPS(url);
           const pathName = utils.getPathname(url);
           for (let i = hostComponents.length - 1; i >= 0; ) {
             if (i === hostComponents.length - 1) {
               domain = hostComponents[hostComponents.length - 1];
             }

             let isForSubdomain;
             if (i != 0) {
               // we can check the db for cookie stored against domain which can be applied for subdomain i.e. a cookie which has domain attribute specified
               // eg: app.citrix.com is a subdomain of citrix.com
               // if a cookie of citrix.com has the domain attribute specified, it can be used for subdomains
               isForSubdomain = true;
             } else {
               isForSubdomain = false;
             }

             const cookieDetails = await this.getCookiesForDomainAndPath({
               domain: domain,
               isForSubdomain: isForSubdomain,
               path: pathName,
               isHTTPS: isSecureURL,
               referenceUri: referenceUri,
             });
             if (cookieDetails && cookieDetails.length > 0) {
               allCookies = allCookies.concat(cookieDetails);
             }
             i--;
             domain = hostComponents[i] + "." + domain;
           }
           return allCookies;
         }

         /// <summary>
         /// get all cookies for given domain and path
         /// </summary>
         async getCookiesForDomainAndPath(
           lookupParams: CookieLookUpParams
         ): Promise<Cookie[]> {
           const pathToFind = (lookupParams.path ? lookupParams.path : "/")
             .replace(/\?.*$/, "")
             .replace(/\#.*$/, ""); //check if this line is needed
           const authStorage = (await AuthStorageWrapper.getInstance()).getInMemoryStorage();
           const cookieDetails: DomainCookieTableEntry = (await authStorage.readEntry(
             storageConstants.DOMAIN_COOKIE_TABLE,
             [lookupParams.domain, lookupParams.referenceUri]
           )) as DomainCookieTableEntry;
           const cookieArray = [];
           if (cookieDetails && Array.isArray(cookieDetails.cookie)) {
            cookieDetails.cookie = await this.decryptCookies(cookieDetails.cookie, lookupParams.referenceUri);
             for (const cookie of cookieDetails.cookie) {
               if (lookupParams.isForSubdomain) {
                 // we are looking for cookies having the domain attribute
                 // hence we continue without pushing the cookie if the domain attribute is missing
                 if (!cookie.domain) {
                   continue;
                 }
               }
               // if a cookie has secure attribute (i.e cookie.secure true) means cookie should be provided only to https. Otherwise it can be provided to both http and https
               if (
                 new RegExp("^" + cookie.path).test(pathToFind) &&
                 !(cookie.secure && !lookupParams.isHTTPS)
               ) {
                 if (!this.isCookieExpired(cookie)) {
                   cookieArray.push(cookie);
                 }
               }
             }
           }
           return cookieArray;
         }

         /// <summary>
         /// get all cookies for given domain
         /// </summary>
         async getCookiesForDomain(
           domain: string,
           referenceUri: string
         ): Promise<DomainCookieTableEntry | null> {
           //ignoring leading dots in domain name as per set-cookie rules
           if (domain[0] === ".") {
             domain = domain.substring(1);
           }
           const authStorage = (await AuthStorageWrapper.getInstance()).getInMemoryStorage();
           const cookieDetails: DomainCookieTableEntry = (await authStorage.readEntry(
             storageConstants.DOMAIN_COOKIE_TABLE,
             [domain, referenceUri]
           )) as DomainCookieTableEntry;
           if (cookieDetails) {
            cookieDetails.cookie = await this.decryptCookies(cookieDetails.cookie, referenceUri);
             return cookieDetails;
           } else {
             return null;
           }
         }

         /// <summary>
         /// check if cookie is expired
         /// </summary>
         isCookieExpired(cookie: Cookie): boolean {
           if (!(cookie["cookieExpiryMillis"]=== null || typeof cookie["cookieExpiryMillis"] === "undefined") &&
              cookie["cookieExpiryMillis"] < Date.now()
           ) {
             return true;
           }
           return false;
         }

         /// <summary>
         /// delete session cookies i.e. cookies without expires and maxAge for a given store using referenceUri
         /// </summary>
         /// <param name="referenceUri">discovery url</param>
         async deleteSessionCookiesForReferenceUri(
           referenceUri: string
         ): Promise<void> {
           const authStorage = (await AuthStorageWrapper.getInstance()).getInMemoryStorage();
           const domainCookies = (await authStorage.readAll(
             storageConstants.DOMAIN_COOKIE_TABLE,
             storageConstants.REFERENCE_URI,
             referenceUri
           )) as DomainCookieTableEntry[];
           if (domainCookies) {
             Logger.getInstance().write(
               logConstants.INFO,
               "Deleting session cookies corresponding to referenceUri " +
                 referenceUri
             );
             for (const cookieDetails of domainCookies) {
               const cookiesWithExpiryorMaxAge = cookieDetails.cookie.filter(
                 (cookieObj) => cookieObj.maxAge || cookieObj.expires
               );
               if (cookiesWithExpiryorMaxAge.length !== 0) {
                 cookieDetails.cookie = cookiesWithExpiryorMaxAge;
                 await authStorage.updateEntry(
                   storageConstants.DOMAIN_COOKIE_TABLE,
                   cookieDetails
                 );
               } else {
                 await authStorage.deleteEntry(
                   storageConstants.DOMAIN_COOKIE_TABLE,
                   [cookieDetails.domain, cookieDetails.referenceUri]
                 );
               }
             }
           }
         }
       }


